import { useCallback } from "react";
import { QueryClient } from "@tanstack/react-query";
import {
  COACHING_GET,
  CONVERSATION_DATA_TABLE_ROWS_GET,
  CONVERSATION_EMAIL_FOLLOWUP,
  CONVERSATION_GET,
  CONVERSATION_SEGMENT_GET,
  DATA_EXTRACTION_STATUS_GET,
  ENTITY_PERMISSIONS_GET,
  SPEAKER_METADATA_GET,
  SUGGESTED_PARTICIPANTS_GET,
} from "@/api";
import { PARTICIPANTS_GET } from "@/api/client/participant";
import {
  Conversation,
  EntityType,
  ProcessingStep,
  ProcessingStepStatus,
  ProcessingStepType,
} from "@/types";
import { CloudEventType, CloudEvent } from "@/constants/websocket-events";
import { useWebSocketSubscription } from "@/context/WebSocketProvider";

interface Params {
  conversationId: string;
  hasNoAccess: boolean;
  queryClient: QueryClient;
  linkedClientIds: Set<string>;
}

export const useConversationWebSocketSubscriptions = ({
  conversationId,
  hasNoAccess,
  queryClient,
  linkedClientIds,
}: Params) => {
  // WebSocket CloudEvent handler for conversation updates
  const handleWebSocketMessage = useCallback(
    (event: CloudEvent) => {
      const eventConversationId = (event.data as { conversationId?: string })
        ?.conversationId;

      // Only handle events for this conversation
      if (eventConversationId !== conversationId) {
        return;
      }

      switch (event.type) {
        // Handle per-step status changes (idempotent overwrite of cached step row).
        case CloudEventType.PROCESSING_STEP_STATUS_CHANGED: {
          const { step, status, errorReason } = event.data as {
            step: ProcessingStepType;
            status: ProcessingStepStatus;
            errorReason?: string;
          };

          queryClient.setQueryData(
            [CONVERSATION_GET, conversationId],
            (oldData: Conversation | undefined) => {
              if (!oldData) return oldData;
              const updatedStep: ProcessingStep = {
                stepType: step,
                status,
                errorReason: errorReason ?? null,
              };
              const existing = oldData.processingSteps ?? [];
              const idx = existing.findIndex((s) => s.stepType === step);
              const nextSteps =
                idx >= 0
                  ? existing.map((s, i) => (i === idx ? updatedStep : s))
                  : [...existing, updatedStep];
              return { ...oldData, processingSteps: nextSteps };
            },
          );

          if (
            status !== ProcessingStepStatus.COMPLETED &&
            status !== ProcessingStepStatus.ERRORED
          ) {
            break;
          }

          switch (step) {
            case ProcessingStepType.SUMMARY:
              queryClient.invalidateQueries({
                queryKey: [CONVERSATION_GET, conversationId],
              });
              queryClient.invalidateQueries({
                queryKey: [SUGGESTED_PARTICIPANTS_GET, conversationId],
              });
              break;
            case ProcessingStepType.TRANSCRIPTION:
              queryClient.invalidateQueries({
                queryKey: [CONVERSATION_SEGMENT_GET, conversationId],
              });
              queryClient.invalidateQueries({
                queryKey: [SPEAKER_METADATA_GET, conversationId],
              });
              break;
            case ProcessingStepType.COACHING:
              queryClient.invalidateQueries({
                queryKey: [COACHING_GET, conversationId],
              });
              break;
            case ProcessingStepType.DATA_EXTRACTION:
              queryClient.invalidateQueries({
                queryKey: [DATA_EXTRACTION_STATUS_GET, conversationId],
              });
              queryClient.invalidateQueries({
                queryKey: [CONVERSATION_DATA_TABLE_ROWS_GET, conversationId],
              });
              break;
            case ProcessingStepType.SENTIMENT:
              queryClient.invalidateQueries({
                queryKey: [CONVERSATION_SEGMENT_GET, conversationId],
              });
              queryClient.invalidateQueries({
                queryKey: [SPEAKER_METADATA_GET, conversationId],
              });
              break;
            case ProcessingStepType.SUGGESTED_PARTICIPANTS:
              queryClient.invalidateQueries({
                queryKey: [SUGGESTED_PARTICIPANTS_GET, conversationId],
              });
              break;
          }
          break;
        }

        case CloudEventType.CONVERSATION_UPDATED: {
          queryClient.invalidateQueries({
            queryKey: [CONVERSATION_GET, conversationId],
          });
          break;
        }

        // Handle conversation status updates
        case CloudEventType.CONVERSATION_STATUS_UPDATED: {
          const { status } = event.data as { status: string };

          // Invalidate conversation query to refetch with new status
          queryClient.invalidateQueries({
            queryKey: [CONVERSATION_GET, conversationId],
          });

          // If status changed to DONE, also invalidate transcript segments and talk-time
          if (status === "DONE") {
            queryClient.invalidateQueries({
              queryKey: [CONVERSATION_SEGMENT_GET, conversationId],
            });
            queryClient.invalidateQueries({
              queryKey: [SPEAKER_METADATA_GET, conversationId],
            });
          }
          break;
        }

        // Handle conversation processing completed
        case CloudEventType.CONVERSATION_PROCESSING_COMPLETED: {
          const { status } = event.data as { status: string };

          // Invalidate conversation query to refetch
          queryClient.invalidateQueries({
            queryKey: [CONVERSATION_GET, conversationId],
          });

          // If status is DONE, also invalidate transcript segments, talk-time, and suggested participants
          if (status === "DONE") {
            queryClient.invalidateQueries({
              queryKey: [CONVERSATION_SEGMENT_GET, conversationId],
            });
            queryClient.invalidateQueries({
              queryKey: [SPEAKER_METADATA_GET, conversationId],
            });
            queryClient.invalidateQueries({
              queryKey: [SUGGESTED_PARTICIPANTS_GET, conversationId],
            });
          }
          break;
        }

        // Handle coaching completed event
        case CloudEventType.COACHING_COMPLETED: {
          // Invalidate both conversation and coaching queries
          queryClient.invalidateQueries({
            queryKey: [CONVERSATION_GET, conversationId],
          });
          queryClient.invalidateQueries({
            queryKey: [COACHING_GET, conversationId],
          });
          break;
        }

        // Handle sentiment analysis completed event
        case CloudEventType.SENTIMENT_COMPLETED: {
          queryClient.invalidateQueries({
            queryKey: [CONVERSATION_SEGMENT_GET, conversationId],
          });
          queryClient.invalidateQueries({
            queryKey: [SPEAKER_METADATA_GET, conversationId],
          });
          break;
        }

        // Handle data extraction completed event
        case CloudEventType.DATA_EXTRACTION_COMPLETED: {
          // Invalidate data extraction related queries
          queryClient.invalidateQueries({
            queryKey: [DATA_EXTRACTION_STATUS_GET, conversationId],
          });
          queryClient.invalidateQueries({
            queryKey: [CONVERSATION_DATA_TABLE_ROWS_GET, conversationId],
          });
          break;
        }
      }
    },
    [conversationId, queryClient],
  );

  // Use global WebSocket subscription for this conversation
  // This automatically subscribes when mounted and unsubscribes when unmounted
  const { isConnected: isWebSocketConnected } = useWebSocketSubscription(
    "conversation",
    conversationId,
    handleWebSocketMessage,
    !hasNoAccess, // enabled
  );

  const handleParticipantWebSocketMessage = useCallback(
    (event: CloudEvent) => {
      const eventConversationId = (event.data as { conversationId?: string })
        ?.conversationId;

      if (eventConversationId !== conversationId) {
        return;
      }

      switch (event.type) {
        case CloudEventType.PARTICIPANT_CREATED:
        case CloudEventType.PARTICIPANT_UPDATED:
        case CloudEventType.PARTICIPANT_DELETED:
          queryClient.invalidateQueries({
            queryKey: [PARTICIPANTS_GET, conversationId],
          });
          queryClient.invalidateQueries({
            queryKey: [CONVERSATION_GET, conversationId],
          });
          break;
      }
    },
    [conversationId, queryClient],
  );

  useWebSocketSubscription(
    "participant",
    conversationId,
    handleParticipantWebSocketMessage,
    !hasNoAccess,
  );

  const handlePermissionWebSocketMessage = useCallback(
    (event: CloudEvent) => {
      const { entityType, entityId } = event.data as {
        entityType?: EntityType;
        entityId?: string;
      };

      if (
        entityType !== EntityType.CONVERSATION ||
        entityId !== conversationId
      ) {
        return;
      }

      switch (event.type) {
        case CloudEventType.PERMISSION_CREATED:
        case CloudEventType.PERMISSION_DELETED:
          queryClient.invalidateQueries({
            queryKey: [
              ENTITY_PERMISSIONS_GET,
              EntityType.CONVERSATION,
              conversationId,
            ],
          });
          break;
      }
    },
    [conversationId, queryClient],
  );

  useWebSocketSubscription(
    "permission",
    conversationId,
    handlePermissionWebSocketMessage,
    !hasNoAccess,
  );

  const handleDataTableRowWebSocketMessage = useCallback(
    (event: CloudEvent) => {
      const eventConversationId = (event.data as { conversationId?: string })
        ?.conversationId;

      if (eventConversationId !== conversationId) {
        return;
      }

      switch (event.type) {
        case CloudEventType.DATA_TABLE_ROW_UPDATED:
        case CloudEventType.DATA_TABLE_ROW_DELETED:
          queryClient.invalidateQueries({
            queryKey: [CONVERSATION_DATA_TABLE_ROWS_GET, conversationId],
          });
          break;
      }
    },
    [conversationId, queryClient],
  );

  useWebSocketSubscription(
    "data-table-row",
    conversationId,
    handleDataTableRowWebSocketMessage,
    !hasNoAccess,
  );

  const handleEmailFollowupWebSocketMessage = useCallback(
    (event: CloudEvent) => {
      const eventConversationId = (event.data as { conversationId?: string })
        ?.conversationId;

      if (eventConversationId !== conversationId) {
        return;
      }

      switch (event.type) {
        case CloudEventType.EMAIL_FOLLOWUP_REGENERATED:
        case CloudEventType.EMAIL_FOLLOWUP_UPDATED:
          queryClient.invalidateQueries({
            queryKey: [CONVERSATION_EMAIL_FOLLOWUP, conversationId],
          });
          break;
      }
    },
    [conversationId, queryClient],
  );

  useWebSocketSubscription(
    "email-followup",
    conversationId,
    handleEmailFollowupWebSocketMessage,
    !hasNoAccess,
  );

  const handleLinkedClientWebSocketMessage = useCallback(
    (event: CloudEvent) => {
      if (event.type !== CloudEventType.CLIENT_UPDATED) {
        return;
      }

      const eventClientId = (event.data as { clientId?: string })?.clientId;
      if (!eventClientId || !linkedClientIds.has(eventClientId)) {
        return;
      }

      queryClient.invalidateQueries({
        queryKey: [CONVERSATION_GET, conversationId],
      });
      queryClient.invalidateQueries({
        queryKey: [PARTICIPANTS_GET, conversationId],
      });
    },
    [conversationId, linkedClientIds, queryClient],
  );

  useWebSocketSubscription(
    "client",
    undefined,
    handleLinkedClientWebSocketMessage,
    !hasNoAccess,
  );

  return { isWebSocketConnected };
};
